import { Button } from '@antimatterhq/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@antimatterhq/ui/components/dialog';
import { Label } from '@antimatterhq/ui/components/label';
import { Form, FormikProvider, useFormik, useFormikContext } from 'formik';
import { toFormikValidate, toFormikValidationSchema } from 'zod-formik-adapter';
import Spacer from '@antimatterhq/ui/components/spacer';
import { WorkspaceConfig_ManagedRetention_Schema } from '@/types/api';
import {
  ManagedRetentionSchema,
  ManagedRetentionSchemaValues,
} from '@/zod/schemas/workspaceConfig/ManagedRetentionSchema';
import { FormError, FormInput } from '@/components/form/form';
import {
  getManagedRetentionInitialSchemaValues,
  setManagedRetentionSchema,
} from '@/lib/workspaceConfig/managedRetention';

export type ManagedRetentionSchemaDialogProps = {
  schema: WorkspaceConfig_ManagedRetention_Schema | undefined;
  isOpen: boolean;
  onCancel: () => void;
  onSave: (schema: WorkspaceConfig_ManagedRetention_Schema) => void;
  isEdit: boolean;
};

const DialogContents = () => {
  const { values, handleChange, handleBlur } =
    useFormikContext<ManagedRetentionSchemaValues>();

  return (
    <>
      <Label htmlFor="schema" id="schema-label" required>
        Name
      </Label>
      <FormInput
        name="schema"
        value={values.schema}
        onChange={handleChange}
        onBlur={handleBlur}
        aria-labelledby="schema-label"
      />
      <FormError name="schema" />

      <Spacer size="base" />

      <Label htmlFor="catalog" id="catalog-label" required>
        Catalog
      </Label>
      <FormInput
        name="catalog"
        value={values.catalog}
        onChange={handleChange}
        onBlur={handleBlur}
        aria-labelledby="catalog-label"
      />
      <FormError name="catalog" />

      <Spacer size="base" />

      <Label htmlFor="column" id="column-label">
        Time column
      </Label>
      <FormInput
        name="column"
        value={values.column}
        onChange={handleChange}
        onBlur={handleBlur}
        aria-labelledby="column-label"
      />
      <FormError name="column" />

      <Label htmlFor="duration" id="duration-label">
        Duration
      </Label>
      <FormInput
        name="duration"
        value={values.duration}
        onChange={handleChange}
        onBlur={handleBlur}
        aria-labelledby="duration-label"
      />
      <FormError name="duration" />
    </>
  );
};

export default function ManagedRetentionSchemaDialog({
  schema,
  isOpen,
  onSave,
  onCancel,
  isEdit,
}: ManagedRetentionSchemaDialogProps) {
  const formik = useFormik({
    initialValues: getManagedRetentionInitialSchemaValues(schema),
    validationSchema: toFormikValidationSchema(ManagedRetentionSchema),
    validate: toFormikValidate(ManagedRetentionSchema),
    onSubmit: async (newValues) => {
      const newSchema = setManagedRetentionSchema(newValues, schema);
      onSave(newSchema);
    },
  });

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent tabIndex={undefined}>
        <DialogHeader>
          <DialogTitle>
            {isEdit ? 'Edit' : 'Add'} managed retention schema
          </DialogTitle>
        </DialogHeader>
        <FormikProvider value={formik}>
          <Form>
            <DialogContents />

            <Spacer size="lg" />

            <DialogFooter>
              <Button type="submit" variant="primary" disabled={!isOpen}>
                Save
              </Button>
            </DialogFooter>
          </Form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  );
}
